import React, { useEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import PendingActionsRounded from '@mui/icons-material/PendingActionsRounded'
import { alpha } from '@mui/material/styles'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../../generic/hooks/useDateFormatter'
import { raisedColor } from '../detailSurfaces'
import { CONTROL_RADIUS } from '../detailControls'
import { ReadOnlyField } from './generalFieldRows'

// The footer of the General block: the unsaved-changes bar, the notice of a concurrent change and the
// delivery date confirmation. It only shows what `useGeneralSave` tells it.
const GeneralSaveBar = ({
  touchedCount, touchedLabels, sheetReady, sheetError, onRetrySheet, saveError, saving, onDiscard, onSave,
  conflict, conflictFieldLabel, deliveryConfirmation, onConfirmDelivery, onCancelDeliveryConfirmation
}) => {
  const { t_ } = useSmartTranslation()
  const { formatDateTime } = useDateFormatter()
  // The conflict and the delivery confirmation render at the end of the block, below the sticky
  // bar: bring them into view when they open.
  const conflictRef = useRef(null)
  const deliveryConfirmationRef = useRef(null)
  useEffect(() => {
    if (conflict) conflictRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
  }, [conflict])
  useEffect(() => {
    if (deliveryConfirmation) deliveryConfirmationRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
  }, [deliveryConfirmation])

  return (
    <>
      {touchedCount > 0 && (
        <Stack
          direction='row'
          spacing={2}
          alignItems='center'
          data-testid='unsaved-changes-bar'
          sx={(theme) => ({
            // Revisión del PO (pasada 24): la barra existía pero era translúcida y vivía DENTRO del
            // scroll, así que se pintaba encima del texto de la última tarjeta y se leía como un
            // fallo. Ahora es un pie opaco, pegado al borde inferior del área de contenido y por
            // encima de ella (`zIndex`), con los márgenes negativos justos para ir de lado a lado.
            position: 'sticky',
            bottom: 0,
            zIndex: 3,
            mt: 3,
            // The bar bleeds to the edges of the content box, whose padding is 2 on xs and 3 from md.
            mx: { xs: -2, md: -3 },
            mb: { xs: -2, md: -3 },
            px: { xs: 2, md: 3 },
            py: 2,
            borderTop: `2px solid ${theme.palette.warning.main}`,
            backgroundColor: raisedColor(theme),
            backgroundImage: `linear-gradient(${alpha(theme.palette.warning.main, 0.12)}, ${alpha(theme.palette.warning.main, 0.12)})`,
            boxShadow: `0 -8px 24px ${alpha(theme.palette.common.black, 0.35)}`
          })}
        >
          <PendingActionsRounded fontSize='small' sx={{ color: 'warning.main' }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant='body2' sx={{ fontWeight: 700 }}>
              {touchedCount === 1 ? t_('1 unsaved change') : t_('{{n}} unsaved changes', { n: touchedCount })}
            </Typography>
            {/* ⚠ La lista nombra TODOS los cambios contados, incluido el campo que nadie ha
            rotulado: si uno se cayera, el contador y los nombres dirían cosas distintas y
            quien lo lea se quedaría buscando el que falta. Eso lo garantiza
            `changedFieldLabels`, que se prueba aparte. */}
            <Typography data-testid='unsaved-changes-fields' variant='caption' sx={{ color: 'text.secondary', display: 'block' }}>
              {touchedLabels.join(', ')}
            </Typography>
            {!sheetReady && !sheetError && (
              <Typography data-testid='save-waiting-for-sheet' variant='caption' sx={{ color: 'text.secondary' }}>
                {t_('Loading the order data before saving...')}
              </Typography>
            )}
            {!sheetReady && sheetError && (
              <Stack direction='row' spacing={1} alignItems='center'>
                <Typography data-testid='save-sheet-error' variant='caption' sx={{ color: 'error.main', fontWeight: 700 }}>
                  {t_('Could not load this block')}
                </Typography>
                <Button data-testid='save-sheet-retry' size='small' onClick={onRetrySheet} sx={{ textTransform: 'none' }}>{t_('Retry')}</Button>
              </Stack>
            )}
            {!!saveError && (
              <Typography data-testid='save-error' variant='caption' sx={{ color: 'error.main', fontWeight: 700 }}>
                {saveError}
              </Typography>
            )}
          </Box>
          <Button data-testid='discard-button' onClick={onDiscard} disabled={saving} sx={{ textTransform: 'none' }}>{t_('Discard')}</Button>
          <Button data-testid='save-button' variant='contained' onClick={onSave} disabled={saving || !sheetReady} sx={{ textTransform: 'none', borderRadius: CONTROL_RADIUS, fontWeight: 700 }}>{saving ? t_('Saving...') : t_('Save changes')}</Button>
        </Stack>
      )}

      {conflict && (
        <Box
          ref={conflictRef}
          data-testid='concurrent-change-conflict'
          sx={(theme) => ({ mt: 2, p: 2, borderRadius: 2, border: `1px solid ${alpha(theme.palette.error.main, 0.35)}`, backgroundColor: alpha(theme.palette.error.main, 0.08) })}
        >
          <Typography variant='body2' sx={{ fontWeight: 700 }}>{t_('Fabrication order was modified by another user')}</Typography>
          {!conflict.degraded && conflict.changedFields.map((field) => (
            <Typography key={field} data-testid={`conflict-field-${field}`} variant='caption' sx={{ display: 'block' }}>{conflictFieldLabel(field)}</Typography>
          ))}
          {conflict.degraded && <Typography data-testid='conflict-degraded' variant='caption'>{t_('The order has changed')}</Typography>}
        </Box>
      )}

      {deliveryConfirmation && (
        <Box
          ref={deliveryConfirmationRef}
          data-testid='delivery-confirmation-dialog'
          sx={(theme) => ({ mt: 2, p: 2, borderRadius: 2, border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`, backgroundColor: raisedColor(theme) })}
        >
          <Typography variant='body2' sx={{ fontWeight: 700 }}>{t_('Fabrication order delivery date requires confirmation')}</Typography>
          {/* RN2/RN3 (E3/A-W1.3, CU-W1): parity with the listing's dialog (FabricationOrder.jsx,
          renderDeliveryConfirmationContent) — a custom processor message is shown verbatim and
          alone (RN14/CA7); otherwise the explanatory literal plus both formatted dates, so the
          user never confirms a new delivery date blind. */}
          {deliveryConfirmation.message
            ? <Typography variant='body2'>{deliveryConfirmation.message}</Typography>
            : (
              <>
                <Typography variant='body2'>{t_('The fabrication order will not be ready by the requested delivery date.')}</Typography>
                <Box data-testid='delivery-confirmation-target-date' sx={{ mt: 2, maxWidth: 360 }}>
                  <ReadOnlyField label={t_('Requested delivery date')}>{formatDateTime(deliveryConfirmation.targetDate)}</ReadOnlyField>
                </Box>
                <Box data-testid='delivery-confirmation-suggested-date' sx={{ mt: 2, maxWidth: 360 }}>
                  <ReadOnlyField label={t_('Next available slot')}>{formatDateTime(deliveryConfirmation.suggestedDate)}</ReadOnlyField>
                </Box>
                <Typography variant='body2' sx={{ mt: 1 }}>{t_('Do you want to confirm this new delivery date?')}</Typography>
              </>
              )}
          <Stack direction='row' spacing={1} sx={{ mt: 2 }}>
            <Button data-testid='delivery-confirmation-accept' variant='contained' onClick={onConfirmDelivery} disabled={saving} sx={{ textTransform: 'none' }}>{t_('Confirm new delivery date')}</Button>
            <Button data-testid='delivery-confirmation-cancel' onClick={onCancelDeliveryConfirmation} sx={{ textTransform: 'none' }}>{t_('CANCEL')}</Button>
          </Stack>
        </Box>
      )}
    </>
  )
}

export default GeneralSaveBar
